/**
 * HTTP client implementation based on ofetch
 */

import { ofetch, type FetchOptions } from 'ofetch';
import type { HTTPClient, ClientConfig } from './types';
import type { InterceptorManager } from './interceptor';

/**
 * Create HTTP client
 */
export function createHTTPClient(
  config: ClientConfig,
  interceptorManager: InterceptorManager
): HTTPClient {
  // Default status validation: 2xx and 304
  const defaultValidateStatus = (status: number) => {
    return (status >= 200 && status < 300) || status === 304;
  };

  // Create ofetch instance
  const fetcher = ofetch.create({
    baseURL: config.baseURL,

    async onRequest({ request, options }) {
      // Add authorization token if available
      if (config.getToken) {
        const token = config.getToken();
        if (token) {
          // Create headers object to avoid type issues
          const headers = new Headers(options.headers);
          headers.set('Authorization', `Bearer ${token}`);
          options.headers = headers;
        }
      }

      // Apply request transformer
      if (config.transformRequest && options.body) {
        options.body = config.transformRequest(options.body);
      }

      // Execute interceptors
      await interceptorManager.executeOnRequest({ request: String(request), options });
    },

    async onRequestError({ request, options, error }) {
      // Execute interceptors
      await interceptorManager.executeOnRequestError({
        request: String(request),
        options,
        error,
      });
    },

    async onResponse({ request, options, response }) {
      // Validate status
      const isValid = interceptorManager.validateStatus({ response });
      const defaultValid = defaultValidateStatus(response.status);

      // If custom validation exists, use it; otherwise use default
      const shouldSucceed = isValid && defaultValid;

      if (!shouldSucceed) {
        // Treat as error response
        await interceptorManager.executeOnResponseError({
          request: String(request),
          options,
          response,
        });

        // Handle 401 unauthorized
        if (response.status === 401 && config.onUnauthorized) {
          config.onUnauthorized();
        }

        // Extract error message from response body if available
        const responseData = response._data;
        const errorMessage =
          responseData?.message ||
          responseData?.error ||
          `HTTP Error: ${response.status} ${response.statusText}`;

        const error = new Error(errorMessage) as Error & { status: number };
        error.status = response.status;
        throw error;
      }

      // Apply response transformer
      if (config.transformResponse && response._data) {
        response._data = config.transformResponse(response._data);
      }

      // Execute success interceptors
      await interceptorManager.executeOnResponse({ request: String(request), options, response });
    },

    async onResponseError({ request, options, response }) {
      // Execute interceptors
      await interceptorManager.executeOnResponseError({
        request: String(request),
        options,
        response,
      });

      // Handle 401 unauthorized
      if (response.status === 401 && config.onUnauthorized) {
        config.onUnauthorized();
      }
    },
  });

  return {
    async get<T = any>(url: string, options?: FetchOptions): Promise<T> {
      return fetcher(url, { ...options, method: 'GET' }) as Promise<T>;
    },

    async post<T = any>(url: string, body?: any, options?: FetchOptions): Promise<T> {
      return fetcher(url, { ...options, method: 'POST', body }) as Promise<T>;
    },

    async put<T = any>(url: string, body?: any, options?: FetchOptions): Promise<T> {
      return fetcher(url, { ...options, method: 'PUT', body }) as Promise<T>;
    },

    async patch<T = any>(url: string, body?: any, options?: FetchOptions): Promise<T> {
      return fetcher(url, { ...options, method: 'PATCH', body }) as Promise<T>;
    },

    async delete<T = any>(url: string, options?: FetchOptions): Promise<T> {
      return fetcher(url, { ...options, method: 'DELETE' }) as Promise<T>;
    },
  };
}
